import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import api from '@/services/api.service';
import assetService from '@/services/asset.service';
import pmvService from '@/services/pmv.service';

const { Title, Text } = Typography;
const { TextArea } = Input;

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[] } })?.data;
  return Array.isArray(ax) ? (ax as T[]) : Array.isArray((ax as { data?: T[] })?.data) ? ((ax as { data: T[] }).data ?? []) : [];
}

const TYPE_LABEL: Record<string, string> = {
  issue: 'Issue to user',
  return: 'Return from user',
  transfer: 'Transfer',
  damage: 'Record damage',
};

export const AssetMovementFormPage: React.FC = () => {
  const { id, type } = useParams<{ id: string; type: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const movementType = (type || 'issue').toLowerCase();

  const assetQ = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetService.getById(id!),
    enabled: Boolean(id),
  });

  const employeesQ = useQuery({
    queryKey: ['emp-asset-mov'],
    queryFn: async () => unwrapArray<{ id: string; full_name: string; employee_code?: string }>(await api.get('/hr/employees')),
  });
  const deptsQ = useQuery({
    queryKey: ['dept-asset-mov'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>(await api.get('/hr/departments')),
  });
  const whQ = useQuery({
    queryKey: ['wh-asset-mov'],
    queryFn: async () => unwrapArray<{ id: string; name: string; code?: string }>(await api.get('/inventory/warehouses')),
  });

  const empOpts = useMemo(
    () => (employeesQ.data ?? []).map((e) => ({ value: e.id, label: `${e.employee_code || ''} ${e.full_name}`.trim() })),
    [employeesQ.data]
  );
  const deptOpts = useMemo(
    () => (deptsQ.data ?? []).map((d) => ({ value: d.id, label: d.name })),
    [deptsQ.data]
  );
  const whOpts = useMemo(
    () => (whQ.data ?? []).map((w) => ({ value: w.id, label: w.code ? `${w.code} — ${w.name}` : w.name })),
    [whQ.data]
  );

  const existing = assetQ.data;

  useEffect(() => {
    if (!existing) return;
    if (movementType === 'issue') {
      form.setFieldsValue({
        to_department_id: existing.department_id,
        to_warehouse_id: existing.warehouse_id,
        to_site: existing.site,
        to_building: existing.building,
        to_room: existing.room,
      });
    } else if (movementType === 'return') {
      form.setFieldsValue({
        to_warehouse_id: existing.warehouse_id,
        to_site: existing.site,
        to_building: existing.building,
        to_room: existing.room,
        return_status: 'active',
      });
    } else if (movementType === 'transfer') {
      form.setFieldsValue({
        to_employee_id: existing.employee_id,
        to_department_id: existing.department_id,
        to_warehouse_id: existing.warehouse_id,
        to_site: existing.site,
        to_building: existing.building,
        to_room: existing.room,
      });
    }
  }, [existing, form, movementType]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (movementType === 'issue') return assetService.issueMovement(id!, body);
      if (movementType === 'return') return assetService.returnMovement(id!, body);
      if (movementType === 'transfer') return assetService.transferMovement(id!, body);
      if (movementType === 'damage') return assetService.damageMovement(id!, body);
      throw new Error('Unknown movement type');
    },
    onSuccess: () => {
      message.success('Movement recorded');
      navigate(`/asset/assets/${id}/edit`);
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  if (assetQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!TYPE_LABEL[movementType]) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Movement" documentNumber="" />}>
        <Typography.Text type="danger">Unknown movement type.</Typography.Text>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={TYPE_LABEL[movementType]}
          documentNumber={existing?.asset_code ?? ''}
        />
      }
    >
      <Title level={5}>{existing?.asset_name}</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          if (movementType === 'issue' || movementType === 'transfer') {
            mut.mutate({
              to_employee_id: v.to_employee_id ?? null,
              to_department_id: v.to_department_id ?? null,
              to_warehouse_id: v.to_warehouse_id ?? null,
              to_site: v.to_site ?? null,
              to_building: v.to_building ?? null,
              to_room: v.to_room ?? null,
              remarks: v.remarks ?? null,
            });
          } else if (movementType === 'return') {
            mut.mutate({
              to_warehouse_id: v.to_warehouse_id ?? null,
              to_site: v.to_site ?? null,
              to_building: v.to_building ?? null,
              to_room: v.to_room ?? null,
              return_status: v.return_status || 'active',
              remarks: v.remarks ?? null,
            });
          } else {
            mut.mutate({
              remarks: v.remarks ?? null,
              severity: v.severity || 'minor',
            });
          }
        }}
      >
        {(movementType === 'issue' || movementType === 'transfer') && (
          <>
            <Form.Item name="to_employee_id" label="To employee">
              <Select allowClear showSearch optionFilterProp="label" options={empOpts} />
            </Form.Item>
            <Form.Item name="to_department_id" label="To department">
              <Select allowClear showSearch optionFilterProp="label" options={deptOpts} />
            </Form.Item>
            <Form.Item name="to_warehouse_id" label="To warehouse">
              <Select allowClear showSearch optionFilterProp="label" options={whOpts} />
            </Form.Item>
            <Form.Item name="to_site" label="Site">
              <Input />
            </Form.Item>
            <Form.Item name="to_building" label="Building">
              <Input />
            </Form.Item>
            <Form.Item name="to_room" label="Room">
              <Input />
            </Form.Item>
          </>
        )}
        {movementType === 'return' && (
          <>
            <Form.Item name="return_status" label="Status after return" initialValue="active" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'idle', label: 'Idle' },
                ]}
              />
            </Form.Item>
            <Form.Item name="to_warehouse_id" label="To warehouse">
              <Select allowClear showSearch optionFilterProp="label" options={whOpts} />
            </Form.Item>
            <Form.Item name="to_site" label="Site">
              <Input />
            </Form.Item>
            <Form.Item name="to_building" label="Building">
              <Input />
            </Form.Item>
            <Form.Item name="to_room" label="Room">
              <Input />
            </Form.Item>
          </>
        )}
        {movementType === 'damage' && (
          <Form.Item name="severity" label="Severity" initialValue="minor">
            <Select
              options={[
                { value: 'minor', label: 'Minor' },
                { value: 'major', label: 'Major' },
                { value: 'critical', label: 'Critical' },
              ]}
            />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Record
          </Button>
          <Button onClick={() => navigate(`/asset/assets/${id}/edit`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const AssetPmvActivateFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [step, setStep] = React.useState(0);
  const ownershipWatch = Form.useWatch('ownership_type', form);
  const classWatch = Form.useWatch('classification_id', form);
  const fleetWatch = Form.useWatch('fleet_number', form);
  const criticalityWatch = Form.useWatch('criticality', form);

  const assetQ = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetService.getById(id!),
    enabled: Boolean(id),
  });
  const classQ = useQuery({
    queryKey: ['pmv-classifications'],
    queryFn: () => pmvService.listClassifications(),
  });

  const subclasses = useMemo(
    () => (classQ.data ?? []).filter((c) => c.parent_id),
    [classQ.data]
  );
  const parents = useMemo(() => {
    const map = new Map((classQ.data ?? []).filter((c) => !c.parent_id).map((c) => [c.id, c]));
    return map;
  }, [classQ.data]);

  const byFamily = useMemo(() => {
    const groups: Record<string, typeof subclasses> = { PLANT: [], MACHINERY: [], VEHICLE: [], OTHER: [] };
    for (const c of subclasses) {
      const fam = (c.class_family || parents.get(c.parent_id!)?.class_family || 'OTHER').toUpperCase();
      const key = fam in groups ? fam : 'OTHER';
      groups[key].push(c);
    }
    return groups;
  }, [subclasses, parents]);

  const selectedClass = subclasses.find((c) => c.id === classWatch);

  React.useEffect(() => {
    const code = assetQ.data?.asset_code;
    if (code && !form.getFieldValue('fleet_number')) {
      form.setFieldValue('fleet_number', code);
    }
  }, [assetQ.data?.asset_code, form]);

  const mut = useMutation({
    mutationFn: (body: {
      asset_id: string;
      classification_id: string;
      ownership_type: string;
      fleet_number?: string | null;
      criticality?: string | null;
    }) => pmvService.activate(body),
    onSuccess: (profile) => {
      message.success('Added to Fleet Register');
      void queryClient.invalidateQueries({ queryKey: ['pmv-profiles-for-assets-list'] });
      void queryClient.invalidateQueries({ queryKey: ['pmv-register'] });
      void queryClient.invalidateQueries({ queryKey: ['pmv-profiles-labels'] });
      void queryClient.invalidateQueries({ queryKey: ['pmv-profile-by-asset', id] });
      navigate(`/asset/pmv/${profile.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Failed to add to fleet'),
  });

  if (assetQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const asset = assetQ.data;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Add to Fleet Register"
          documentNumber={asset?.asset_code ?? ''}
          statusTag={<Tag color="orange">Asset → PMV</Tag>}
          subtitle={
            <Text type="secondary">
              Enable Plant, Machinery &amp; Vehicle operations for this enterprise asset. Identity stays on the Asset
              record.
            </Text>
          }
          primaryActions={
            <Button onClick={() => navigate(`/asset/assets/${id}`)}>Back to asset</Button>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 880 }}>
        <Card size="small" style={{ background: '#fafafa' }}>
          <Space direction="vertical" size={2}>
            <Text type="secondary">Enterprise asset</Text>
            <Title level={4} style={{ margin: 0 }}>
              {asset?.asset_code} — {asset?.asset_name}
            </Title>
            <Text type="secondary">
              {[asset?.manufacturer, asset?.model].filter(Boolean).join(' · ') || 'Make / model not set'}
              {asset?.serial_number ? ` · Serial ${asset.serial_number}` : ''}
              {asset?.status ? ` · Asset status ${asset.status.replace(/_/g, ' ')}` : ''}
            </Text>
          </Space>
        </Card>

        <Steps
          current={step}
          onChange={(s) => setStep(s)}
          items={[
            { title: 'Classification', description: 'What kind of unit?' },
            { title: 'Operations', description: 'Ownership & unit ID' },
            { title: 'Confirm', description: 'Add to fleet' },
          ]}
        />

        <Form
          form={form}
          layout="vertical"
          preserve
          initialValues={{ ownership_type: 'OWNED', fleet_number: asset?.asset_code }}
          onFinish={() => {
            // Wizard unmounts prior steps; getFieldsValue(true) keeps preserved store values.
            const v = form.getFieldsValue(true) as {
              classification_id?: string;
              ownership_type?: string;
              fleet_number?: string;
              criticality?: string;
            };
            if (!v.classification_id) {
              message.error('Select a classification');
              setStep(0);
              return;
            }
            if (!v.ownership_type) {
              message.error('Select ownership');
              setStep(1);
              return;
            }
            mut.mutate({
              asset_id: id!,
              classification_id: v.classification_id,
              ownership_type: v.ownership_type,
              fleet_number: v.fleet_number || null,
              criticality: v.criticality || null,
            });
          }}
        >
          {/* Keep fields mounted across steps so submit always includes them */}
          <Form.Item name="classification_id" rules={[{ required: true, message: 'Select a classification' }]} hidden>
            <Input />
          </Form.Item>
          <Form.Item name="ownership_type" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="fleet_number" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="criticality" hidden>
            <Input />
          </Form.Item>

          <div style={{ display: step === 0 ? 'block' : 'none' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Pick the operational class"
                description="This drives technical attributes, matching, inspections, and hire rates. It does not change the Asset financial record."
              />
              {(['MACHINERY', 'PLANT', 'VEHICLE', 'OTHER'] as const).map((fam) => {
                const items = byFamily[fam];
                if (!items?.length) return null;
                return (
                  <div key={fam}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                      {fam === 'OTHER' ? 'Other' : fam.charAt(0) + fam.slice(1).toLowerCase()}
                    </Title>
                    <Row gutter={[12, 12]}>
                      {items.map((c) => {
                        const selected = classWatch === c.id;
                        return (
                          <Col xs={24} sm={12} md={8} key={c.id}>
                            <Card
                              size="small"
                              hoverable
                              onClick={() => form.setFieldValue('classification_id', c.id)}
                              style={{
                                borderColor: selected ? '#1677ff' : undefined,
                                background: selected ? '#e6f4ff' : undefined,
                                cursor: 'pointer',
                                height: '100%',
                              }}
                            >
                              <Space direction="vertical" size={0}>
                                <Text strong>{c.name}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {c.code}
                                </Text>
                              </Space>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })}
              {!subclasses.length && !classQ.isLoading && (
                <Alert
                  type="warning"
                  showIcon
                  message="No classifications configured"
                  description="Ask an administrator to open PMV Configuration and ensure classifications are seeded."
                  action={
                    <Button size="small" onClick={() => navigate('/asset/pmv/setup')}>
                      Configuration
                    </Button>
                  }
                />
              )}
              <Button type="primary" disabled={!classWatch} onClick={() => setStep(1)}>
                Continue
              </Button>
            </Space>
          </div>

          <div style={{ display: step === 1 ? 'block' : 'none' }}>
            <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
              <Form.Item label="How is this unit provided?" required>
                <Select
                  value={ownershipWatch || 'OWNED'}
                  onChange={(val) => form.setFieldValue('ownership_type', val)}
                  options={[
                    { value: 'OWNED', label: 'Owned' },
                    { value: 'RENTED', label: 'Rented' },
                    { value: 'LEASED', label: 'Leased' },
                    { value: 'CLIENT_PROVIDED', label: 'Client provided' },
                    { value: 'SUBCONTRACTOR_PROVIDED', label: 'Subcontractor provided' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Fleet / unit number"
                extra="Operational call-sign on site. Defaults to asset code; you can change it."
              >
                <Input
                  value={fleetWatch ?? ''}
                  placeholder="e.g. EXC-014"
                  onChange={(e) => form.setFieldValue('fleet_number', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="Operational criticality">
                <Select
                  allowClear
                  placeholder="Optional"
                  value={criticalityWatch ?? undefined}
                  onChange={(val) => form.setFieldValue('criticality', val ?? null)}
                  options={[
                    { value: 'LOW', label: 'Low' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HIGH', label: 'High' },
                    { value: 'CRITICAL', label: 'Critical' },
                  ]}
                />
              </Form.Item>
              <Space>
                <Button onClick={() => setStep(0)}>Back</Button>
                <Button
                  type="primary"
                  onClick={() => {
                    if (!form.getFieldValue('ownership_type')) {
                      message.error('Select ownership');
                      return;
                    }
                    setStep(2);
                  }}
                >
                  Continue
                </Button>
              </Space>
            </Space>
          </div>

          <div style={{ display: step === 2 ? 'block' : 'none' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card title="Confirm — add to Fleet Register" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Asset">
                    {asset?.asset_code} — {asset?.asset_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Classification">
                    {selectedClass ? `${selectedClass.name} (${selectedClass.code})` : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ownership">
                    {String(ownershipWatch || 'OWNED').replace(/_/g, ' ')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fleet / unit number">{fleetWatch || asset?.asset_code || '—'}</Descriptions.Item>
                </Descriptions>
              </Card>
              <Alert
                type="success"
                showIcon
                message="After activation"
                description="The unit appears on the Fleet Register and opens as an Equipment Passport. Asset financial identity is unchanged."
              />
              <Space>
                <Button onClick={() => setStep(1)}>Back</Button>
                <Button type="primary" htmlType="submit" loading={mut.isPending} size="large">
                  Add to Fleet Register
                </Button>
                <Button onClick={() => navigate(`/asset/assets/${id}`)}>Cancel</Button>
              </Space>
            </Space>
          </div>
        </Form>
      </Space>
    </WorkspaceLayout>
  );
};
